import type { Pose } from "@soheil/core";

/**
 * سهیل — بازسازی‌شده از «طرح C» تأییدشدهٔ طراح.
 *
 * مختصات عیناً از Soheil_Direction_C.svg آمده‌اند (فضای ۸۰۰×۸۰۰) تا هیچ
 * خطای تبدیل وارد نشود. چیزی که به آن اضافه شده، فقط «مفصل» است:
 * دست‌ها از شانه می‌چرخند و پراپ‌ها روشن/خاموش می‌شوند.
 *
 * نامتقارنی عمدی طرح C حفظ شده — گوش راست بزرگ‌تر و بالاتر، چشم چپ کمی
 * بالاتر. همین چیزی است که به آن شخصیت می‌دهد؛ «مرتب» کردنش خرابش می‌کند.
 */

const FUR = "#B98455";
const FUR_D = "#A9764B";
const CREAM = "#FAF6EE";
const SAND = "#E8CDA8";
const INK = "#2D2A32";
const GOLD = "#D9963A";

/** ستارهٔ سینه — همان چندضلعی طرح C */
const CHEST_STAR =
  "400,457 408.23,476.67 429.48,478.42 413.31,492.33 418.22,513.08 " +
  "400,502 381.78,513.08 386.69,492.33 370.52,478.42 391.77,476.67";

/** زاویهٔ دست‌ها در هر ژست — درجه، حول شانه */
const ARMS: Record<Pose, [number, number]> = {
  idle:      [0, 0],
  studying:  [-16, 14],
  thinking:  [-155, 0],
  break:     [46, -46],
  drinking:  [0, -66],
  sleepy:    [4, -4],
  celebrate: [135, -133],
  exercise:  [75, -75],
};

/** چرخش کل بدن */
const TILT: Record<Pose, number> = {
  idle: 0, studying: -3, thinking: -3, break: 2,
  drinking: 0, sleepy: 6, celebrate: 0, exercise: 0,
};

export function SoheilRig({ scale = 1, x = 0, y = 0 }: { scale?: number; x?: number; y?: number }) {
  return (
    <g transform={`translate(${x},${y}) scale(${scale})`}>
      <g className="soheil">
        {/* سایه — با بدن حرکت نمی‌کند */}
        <ellipse cx="400" cy="640" rx="200" ry="24" fill={INK} opacity="0.08" />

        <g className="body-tilt">
          <g className="breathe">
            {/* بدن و کاپشن */}
            <ellipse cx="398" cy="510" rx="160" ry="158" fill={FUR} />
            {/* پاها — تا حاشیهٔ خرمایی «حلقه» دیده نشود */}
            <ellipse cx="336" cy="636" rx="52" ry="34" fill={FUR} />
            <ellipse cx="462" cy="636" rx="52" ry="34" fill={FUR} />
            <ellipse cx="336" cy="640" rx="30" ry="18" fill={SAND} opacity="0.7" />
            <ellipse cx="462" cy="640" rx="30" ry="18" fill={SAND} opacity="0.7" />
            <ellipse cx="400" cy="500" rx="145" ry="128" fill={CREAM} />
            <ellipse cx="398" cy="428" rx="108" ry="40" fill={SAND} opacity="0.55" />

            {/* دست‌ها — کپسول، مفصل فقط از شانه */}
            <g className="arm-r">
              <path d="M305 445L278 548" stroke={SAND} strokeWidth="80" strokeLinecap="round" fill="none" opacity="0.75" />
              <path d="M305 445L278 548" stroke={CREAM} strokeWidth="70" strokeLinecap="round" fill="none" />
              <circle cx="278" cy="548" r="36" fill={FUR_D} />
            </g>
            <g className="arm-l">
              <path d="M487 455L516 552" stroke={SAND} strokeWidth="80" strokeLinecap="round" fill="none" opacity="0.75" />
              <path d="M487 455L516 552" stroke={CREAM} strokeWidth="70" strokeLinecap="round" fill="none" />
              <circle cx="516" cy="552" r="36" fill={FUR_D} />
            </g>

            {/* ستارهٔ سینه — نشانهٔ برند */}
            <polygon className="chest-star" points={CHEST_STAR} fill={GOLD} />

            {/* گوش‌ها — نامتقارن، عمدی */}
            <circle cx="275" cy="142" r="66" fill={FUR} />
            <circle cx="275" cy="146" r="33" fill={FUR_D} />
            <circle cx="535" cy="170" r="60" fill={FUR} />
            <circle cx="535" cy="173" r="30" fill={FUR_D} />

            {/* سر */}
            <ellipse cx="400" cy="265" rx="174" ry="166" fill={FUR} />
            <ellipse cx="400" cy="334" rx="76" ry="50" fill={SAND} />

            {/* ابروها */}
            <rect x="326" y="237" width="41" height="7" rx="4" fill={FUR_D} transform="rotate(-11 347 241)" />
            <rect x="429" y="244" width="41" height="7" rx="4" fill={FUR_D} transform="rotate(8 450 248)" />

            {/* چشم‌ها */}
            <g className="blink" style={{ transformOrigin: "400px 278px" }}>
              <ellipse className="eye-open" cx="348" cy="273" rx="14" ry="21" fill={INK} />
              <ellipse className="eye-open" cx="448" cy="282" rx="14" ry="21" fill={INK} />
            </g>
            <path className="eye-shut" d="M332 275q16 15 32 0" stroke={INK} strokeWidth="8" fill="none" strokeLinecap="round" />
            <path className="eye-shut" d="M432 284q16 15 32 0" stroke={INK} strokeWidth="8" fill="none" strokeLinecap="round" />

            {/* پوزه */}
            <ellipse cx="400" cy="317" rx="19" ry="13" fill={INK} />
            <rect x="397" y="327" width="6" height="20" rx="3" fill={INK} />
            <ellipse cx="386" cy="349" rx="18" ry="7" fill={INK} transform="rotate(10 386 349)" />
            <ellipse cx="414" cy="349" rx="18" ry="7" fill={INK} transform="rotate(-10 414 349)" />
            <ellipse cx="400" cy="344" rx="19" ry="9" fill={SAND} />

            {/* لپ‌ها — طلایی کمیاب، فقط ۱۰٪ */}
            <ellipse cx="315" cy="337" rx="14" ry="9" fill={GOLD} opacity="0.10" />
            <ellipse cx="485" cy="337" rx="14" ry="9" fill={GOLD} opacity="0.10" />
          </g>

          {/* ── پراپ‌ها ── */}
          <g className="prop p-book">
            <rect x="272" y="600" width="256" height="46" rx="18" fill={CREAM} />
            <rect x="394" y="602" width="12" height="42" rx="6" fill={SAND} />
            <rect x="470" y="586" width="86" height="11" rx="5.5" fill={GOLD} transform="rotate(-8 513 591)" />
          </g>

          <g className="prop p-cup">
            <rect x="492" y="448" width="64" height="64" rx="15" fill={SAND} />
            <rect x="497" y="453" width="54" height="54" rx="12" fill={CREAM} />
            <path d="M556 466h16a16 16 0 010 32h-16" stroke={SAND} strokeWidth="12" fill="none" />
            <path d="M508 432q10-22 20 0M530 432q10-22 20 0" stroke={SAND} strokeWidth="7" fill="none" strokeLinecap="round" opacity="0.8" />
          </g>

          <g className="prop p-think">
            <circle cx="588" cy="150" r="13" fill={CREAM} />
            <circle cx="618" cy="112" r="18" fill={CREAM} />
            <circle cx="658" cy="62" r="25" fill={CREAM} />
          </g>

          <g className="prop p-zzz zzz">
            <text x="572" y="140" fontFamily="Vazirmatn, sans-serif" fontWeight="700" fontSize="46" fill={SAND}>z</text>
            <text x="612" y="96" fontFamily="Vazirmatn, sans-serif" fontWeight="700" fontSize="58" fill={SAND}>z</text>
            <text x="660" y="44" fontFamily="Vazirmatn, sans-serif" fontWeight="700" fontSize="72" fill={SAND}>Z</text>
          </g>

          <g className="prop p-stars">
            <g><polygon points="180,150 189,172 213,174 195,190 200,214 180,201 160,214 165,190 147,174 171,172" fill={GOLD} /></g>
            <g><polygon points="640,120 647,137 665,139 651,151 655,169 640,159 625,169 629,151 615,139 633,137" fill="#F2D9AE" /></g>
            <g><polygon points="400,42 409,64 433,66 415,82 420,106 400,93 380,106 385,82 367,66 391,64" fill={GOLD} /></g>
          </g>

          <g className="prop p-motion" stroke={SAND} strokeWidth="11" strokeLinecap="round" opacity="0.65">
            <path d="M150 440h44M128 500h38M158 556h32" />
            <path d="M650 440h-44M672 500h-38M642 556h-32" />
          </g>
        </g>
      </g>
    </g>
  );
}

/** نسخهٔ مستقل — برای کارت‌ها و فهرست ژست‌ها */
export function Soheil({ pose = "idle", className = "" }: { pose?: Pose; className?: string }) {
  return (
    <svg viewBox="110 10 580 700" className={`soheil-svg ${className}`} data-pose={pose} aria-hidden="true">
      <SoheilRig />
    </svg>
  );
}

export { ARMS as SOHEIL_ARMS, TILT as SOHEIL_TILT };
